import { Button, networkIconFor } from '@alison/ui';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import type { Network } from './flightCheckMocks.js';
import { formatScore, scoreText, scoreTint } from './scoreScale.js';

export const CARD_SX: SxProps<Theme> = {
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'divider',
  overflow: 'hidden',
};

export const TEXT_STRONG = '#31343A';

export const TEXT_SOFT = '#818897';

export const DIVIDER = '#E9ECEF';

export function NetworkIcon({ network, size }: { network: Network; size: number }): ReactNode {
  const Icon = networkIconFor(network);
  if (Icon === undefined) return null;
  return <Icon aria-label={network} sx={{ width: size, height: size, flexShrink: 0 }} />;
}

export function ScorePill({ score }: { score: number }): ReactNode {
  const theme = useTheme();
  return (
    <Box
      component="span"
      sx={{
        px: '8px',
        py: '3px',
        borderRadius: '10px',
        bgcolor: scoreTint(theme, score),
        color: scoreText(theme, score),
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 'normal',
        flexShrink: 0,
      }}
    >
      {formatScore(score)}
    </Box>
  );
}

export function ScoreValue({
  score,
  size,
  weight = 500,
}: {
  score: number;
  size: number;
  weight?: number;
}): ReactNode {
  const theme = useTheme();
  return (
    <Typography
      component="span"
      sx={{
        fontSize: size,
        fontWeight: weight,
        lineHeight: 'normal',
        color: scoreText(theme, score),
      }}
    >
      {formatScore(score)}
    </Typography>
  );
}

export function FilterChip({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        px: '11px',
        py: '9px',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'border.default',
        borderRadius: '9px',
        flexShrink: 0,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: 9,
          fontWeight: 600,
          letterSpacing: '0.9px',
          textTransform: 'uppercase',
          color: 'text.muted',
          lineHeight: 'normal',
        }}
      >
        {label}
      </Typography>
      <Typography component="span" sx={{ fontSize: 12, color: TEXT_STRONG, lineHeight: 'normal' }}>
        {value}
      </Typography>
      <KeyboardArrowDown sx={{ fontSize: 16, color: 'text.muted' }} />
    </Box>
  );
}

export function OutlineButton({ children }: { children: ReactNode }): ReactNode {
  return (
    <Button
      variant="outlined"
      size="small"
      sx={{
        minHeight: 36,
        px: '14px',
        borderColor: 'border.default',
        fontSize: 12,
        fontWeight: 500,
        color: 'text.secondary',
        whiteSpace: 'nowrap',
        flexShrink: 0,
        '&:hover': { borderColor: 'border.strong', bgcolor: 'action.hover' },
      }}
    >
      {children}
    </Button>
  );
}
